/**
 * WebBillingGuard — enforcement da subscrição no Dashboard web.
 *
 * Consome `GET /api/billing/status` e reage a 3 estados:
 *   • `access === 'granted'` (trial activo, sub activa, exempt, OEM lifetime)
 *       → não renderiza nada, deixa a app funcionar normalmente.
 *   • `access === 'grace'` (trial/sub expirou mas ainda há X dias de tolerância)
 *       → mostra banner amarelo no topo com countdown + CTA "Subscrever".
 *   • `access === 'blocked'` (fim do período de graça)
 *       → mostra overlay em ecrã inteiro que impede uso do dashboard.
 *
 * Nota: Os dispositivos Tectur (GPS OEM) recebem `plan === 'oem_lifetime'`
 * quando têm chave OEM ativada — isto é `granted` sem expiração. Todos os
 * telemóveis/computadores normais entram no ciclo trial(10d)+grace(3d)+blocked.
 *
 * O componente é usado dentro do Dashboard depois do login com sucesso.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Lock, Sparkles, Gift, Rocket } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WebBillingGuard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then((res) => { if (mounted) setStatus(res.data || {}); })
      .catch(() => { if (mounted) setStatus({ access: 'unknown' }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || !status) return null;

  const { access, days_left_grace, grace_ends_at, plan } = status;

  // BLOCKED — overlay bloqueante em ecrã inteiro
  if (access === 'blocked') {
    return (
      <div
        className="fixed inset-0 z-[10000] bg-slate-900/95 flex items-center justify-center p-6"
        data-testid="billing-blocked-overlay"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-2">Acesso Bloqueado</h2>
          <p className="text-slate-600 mb-6 text-sm">
            O teu período gratuito de <strong>10 dias</strong> + <strong>3 dias de graça</strong> chegaram ao fim.
            Para continuar a usar o Tectur 4x4, subscreve o plano <strong>PRO</strong>.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Plano Mensal</span>
              <span className="font-bold text-slate-900">€4,00/mês</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Plano Anual</span>
              <span className="font-bold text-slate-900">€39,99/ano</span>
            </div>
          </div>
          <button
            type="button"
            className="w-full bg-primary text-primary-foreground font-bold py-3 rounded-lg hover:opacity-90 transition mb-2"
            onClick={() => window.open('mailto:tectur@tectur.pt?subject=Subscrição Tectur PRO', '_blank')}
            data-testid="billing-contact-btn"
          >
            Contactar para Subscrever
          </button>
          <p className="text-[11px] text-slate-500 mt-4">
            Contacto: <a href="mailto:tectur@tectur.pt" className="underline">tectur@tectur.pt</a>
          </p>
        </div>
      </div>
    );
  }

  // GRACE — banner no topo (dismissível localmente na sessão)
  if (access === 'grace' && !dismissed) {
    return (
      <div
        className="w-full bg-amber-500 text-white px-4 py-2.5 flex items-center gap-3 text-sm"
        data-testid="billing-grace-banner"
      >
        <AlertTriangle className="h-5 w-5 shrink-0" />
        <div className="flex-1">
          <strong>Período gratuito terminou.</strong>{' '}
          Restam <strong>{days_left_grace ?? '—'} {(days_left_grace === 1) ? 'dia' : 'dias'}</strong> antes do bloqueio total.{' '}
          Subscreve <strong>Tectur PRO</strong> por €4,00/mês para continuar sem interrupções.
        </div>
        <a
          href="mailto:tectur@tectur.pt?subject=Subscrição Tectur PRO"
          className="bg-white text-amber-700 font-bold px-3 py-1 rounded-md hover:bg-amber-50 transition text-xs"
          data-testid="billing-grace-cta"
        >
          Subscrever
        </a>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-1 hover:bg-white/10 rounded p-1"
          aria-label="Fechar"
          data-testid="billing-grace-dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  // OEM lifetime — badge discreto (opcional)
  if (access === 'granted' && plan === 'oem_lifetime') {
    return (
      <div
        className="w-full bg-emerald-50 text-emerald-800 border-b border-emerald-200 px-4 py-1.5 flex items-center gap-2 text-xs"
        data-testid="billing-oem-banner"
      >
        <Sparkles className="h-3.5 w-3.5" />
        <span>
          Dispositivo <strong>Tectur GPS</strong> — Acesso <strong>vitalício</strong> incluído (OEM). Sem subscrição necessária.
        </span>
      </div>
    );
  }

  // TRIAL — pill discreto azul-claro com countdown
  if (access === 'granted' && plan === 'trial') {
    return (
      <div
        className="w-full bg-sky-50 text-sky-900 border-b border-sky-200 dark:bg-sky-950/60 dark:text-sky-100 dark:border-sky-900 px-4 py-1.5 flex items-center gap-2 text-xs"
        data-testid="billing-trial-pill"
      >
        <Gift className="h-3.5 w-3.5 text-sky-600" />
        <span className="flex-1">
          <strong>Trial gratuito</strong>
          {status?.days_left != null && (
            <> · restam <strong>{status.days_left} {status.days_left === 1 ? 'dia' : 'dias'}</strong></>
          )}
          {status?.trial_ends_at && (
            <span className="text-sky-700/70"> · termina {new Date(status.trial_ends_at).toLocaleDateString('pt-PT')}</span>
          )}
        </span>
        <a
          href="mailto:tectur@tectur.pt?subject=Subscrição Tectur PRO"
          className="text-sky-800 font-bold hover:underline"
          data-testid="billing-trial-cta"
        >
          Subscrever
        </a>
      </div>
    );
  }

  // SUBSCRIPTION active — pill discreto verde com data de renovação
  if (access === 'granted' && plan === 'subscription') {
    return (
      <div
        className="w-full bg-emerald-50 text-emerald-900 border-b border-emerald-200 px-4 py-1.5 flex items-center gap-2 text-xs"
        data-testid="billing-pro-pill"
      >
        <Rocket className="h-3.5 w-3.5 text-emerald-600" />
        <span className="flex-1">
          <strong>Tectur PRO</strong>
          {status?.expires_at && (
            <> · renova em <strong>{new Date(status.expires_at).toLocaleDateString('pt-PT')}</strong></>
          )}
        </span>
      </div>
    );
  }

  // EXEMPT — badge violeta
  if (access === 'granted' && plan === 'exempt') {
    return (
      <div
        className="w-full bg-violet-50 text-violet-900 border-b border-violet-200 px-4 py-1.5 hidden md:flex items-center gap-2 text-xs"
        data-testid="billing-exempt-badge"
      >
        <Sparkles className="h-3.5 w-3.5 text-violet-600" />
        <span>
          <strong>Acesso ilimitado</strong> — Conta isenta de subscrição.
        </span>
      </div>
    );
  }

  // FREE_ROLE — admin/staff/group manager (acesso sem limite pelo papel)
  if (access === 'granted' && plan === 'free_role') {
    return (
      <div
        className="w-full bg-slate-100 text-slate-800 border-b border-slate-200 px-4 py-1.5 hidden md:flex items-center gap-2 text-xs"
        data-testid="billing-role-badge"
      >
        <Sparkles className="h-3.5 w-3.5 text-slate-600" />
        <span>
          <strong>Acesso ilimitado</strong> — Conta admin/staff. Não requer subscrição.
        </span>
      </div>
    );
  }

  return null;
};

export default WebBillingGuard;
